import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ImagePlus, Plus, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { imgSrc, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { LocationPicker } from "@/components/CampusMap";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Bird", "Mammal", "Amphibian", "Reptile", "Insect", "Fish", "Other"];

export default function NewPost() {
  const { status, user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const fileRef = useRef(null);

  const [title, setTitle] = useState("");
  const [species, setSpecies] = useState("");
  const [category, setCategory] = useState("Bird");
  const [locationName, setLocationName] = useState("");
  const [position, setPosition] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [chapters, setChapters] = useState([{ heading: "", body: "" }]);
  const [extraPhotos, setExtraPhotos] = useState([]);
  const [extraUrl, setExtraUrl] = useState("");
  const [usersList, setUsersList] = useState([]);
  const [authorId, setAuthorId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loadingEntry, setLoadingEntry] = useState(!!editId);
  const [photoFile, setPhotoFile] = useState(null);
  const [identifying, setIdentifying] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiProgress, setAiProgress] = useState(null);
  const [cloudOffer, setCloudOffer] = useState(false);

  useEffect(() => {
    if (status === "guest") navigate("/login");
  }, [status, navigate]);

  useEffect(() => {
    if (!editId || status !== "authed" || !user) return;
    api.get(`/animals/${editId}`)
      .then(({ data }) => {
        if (user.role !== "admin" && user.user_id !== data.author_id) {
          toast.error("Only the author or an admin can edit this entry");
          navigate(`/animal/${editId}`);
          return;
        }
        setTitle(data.title);
        setSpecies(data.species === "Unknown species" ? "" : data.species);
        setCategory(data.category || "Bird");
        setLocationName(data.location_name || "");
        if (data.lat != null && data.lng != null) setPosition([data.lat, data.lng]);
        setImageUrl(data.image_url || "");
        setChapters(data.chapters && data.chapters.length ? data.chapters : [{ heading: "", body: "" }]);
        setExtraPhotos(data.photos || []);
        setAuthorId(data.author_id || "");
        if (user.role === "admin") {
          api.get("/users").then(({ data: us }) => setUsersList(us)).catch(() => {});
        }
      })
      .catch(() => {
        toast.error("Could not load that entry");
        navigate("/");
      })
      .finally(() => setLoadingEntry(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, status, user]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setPhotoFile(file);
    setAiSuggestion(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.url);
      toast.success("Photo added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const applySuggestion = (data) => {
    if (!data || !data.species) {
      toast.error("The AI could not spot an animal — fill it in yourself");
      return;
    }
    setAiSuggestion(data);
    setSpecies(data.species);
    if (data.category && CATEGORIES.includes(data.category)) setCategory(data.category);
    toast.success(`Looks like ${data.species}`);
  };

  const identifyWithCloud = async () => {
    setIdentifying(true);
    setAiSuggestion(null);
    try {
      const fd = new FormData();
      if (photoFile) fd.append("file", photoFile);
      else fd.append("image_url", imageUrl);
      const { data } = await api.post("/identify", fd, { headers: { "Content-Type": "multipart/form-data" } });
      applySuggestion(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setIdentifying(false);
    }
  };

  const handleExtraFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setExtraPhotos((prev) => [...prev, data.url]);
      }
      toast.success(list.length > 1 ? `${list.length} photos added` : "Photo added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const addExtraPhotoUrl = () => {
    const url = extraUrl.trim();
    if (!url) return;
    setExtraPhotos((prev) => [...prev, url]);
    setExtraUrl("");
  };

  const identifySpecies = async () => {
    if (!photoFile && !imageUrl) {
      toast.error("Choose a photo or paste a link first");
      return;
    }
    setIdentifying(true);
    setAiSuggestion(null);
    setAiProgress(null);
    setCloudOffer(false);
    try {
      let blob = photoFile;
      if (!blob) {
        const res = await fetch(imgSrc(imageUrl));
        if (!res.ok) throw new Error("fetch failed");
        blob = await res.blob();
      }
      if (typeof window === "undefined" || !("LanguageModel" in window)) {
        setCloudOffer(true);
        toast.info("This browser has no built-in AI — use Chrome 148+ or Edge, or cloud AI below");
        return;
      }
      const opts = {
        expectedInputs: [{ type: "text", languages: ["en"] }, { type: "image" }],
        expectedOutputs: [{ type: "text", languages: ["en"] }],
      };
      const availability = await window.LanguageModel.availability(opts);
      if (availability === "unavailable") {
        setCloudOffer(true);
        toast.info("On-device AI isn't available on this device — you can use cloud AI below");
        return;
      }
      const session = await window.LanguageModel.create({
        ...opts,
        monitor: (m) => m.addEventListener("downloadprogress", (e) => setAiProgress(Math.round((e.loaded || 0) * 100))),
      });
      const schema = {
        type: "object",
        properties: {
          species: { type: ["string", "null"] },
          category: { type: "string", enum: CATEGORIES },
          confidence: { type: "string", enum: ["high", "medium", "low"] },
          note: { type: "string" },
        },
        required: ["species", "category", "confidence", "note"],
      };
      const messages = [{
        role: "user",
        content: [
          { type: "text", value: "You are an expert field naturalist helping students catalog school campus wildlife. Identify the animal in this photo. Return JSON with: species (common name, or null if no animal is clearly visible), category (exactly one of Bird, Mammal, Amphibian, Reptile, Insect, Fish, Other), confidence (high, medium or low), note (one short sentence naming the visible field marks)." },
          { type: "image", value: blob },
        ],
      }];
      let raw;
      try {
        raw = await session.prompt(messages, { responseConstraint: schema });
      } catch {
        raw = await session.prompt(messages);
      }
      if (session.destroy) session.destroy();
      const data = JSON.parse(raw.replace(/```json|```/g, "").trim());
      applySuggestion(data);
    } catch (e) {
      console.warn("on-device identify failed", e);
      setCloudOffer(true);
      toast.error("On-device identification didn't work here — you can use cloud AI below");
    } finally {
      setIdentifying(false);
      setAiProgress(null);
    }
  };

  const setChapter = (i, key, value) =>
    setChapters((prev) => prev.map((c, idx) => (idx === i ? { ...c, [key]: value } : c)));

  const submit = async (e) => {
    e.preventDefault();
    if (!position) {
      toast.error("Tap the map to pin where you spotted it");
      return;
    }
    if (!imageUrl) {
      toast.error("Add a photograph first");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title,
        species,
        category,
        location_name: locationName,
        lat: position[0],
        lng: position[1],
        image_url: imageUrl,
        chapters: chapters.filter((c) => c.body.trim()),
        photos: extraPhotos,
        author_id: authorId || null,
      };
      if (editId) {
        await api.put(`/animals/${editId}`, payload);
        toast.success("Entry updated");
        navigate(`/animal/${editId}`);
      } else {
        const { data } = await api.post("/animals", payload);
        toast.success("Entry added to the journal");
        navigate(`/animal/${data.post_id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setSubmitting(false);
    }
  };

  if (status !== "authed" || loadingEntry) {
    return (
      <div className="min-h-screen" data-testid="new-post-checking">
        <Navbar />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="new-post-page">
      <Navbar />
      <motion.main
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mx-auto max-w-3xl px-6 pb-32 pt-32"
      >
        <p className="text-xs font-bold uppercase tracking-[0.35em] text-primary">{editId ? "Edit entry" : "New field note"}</p>
        <h1 className="mt-4 font-serif text-5xl font-semibold tracking-tighter" data-testid="new-post-heading">
          {editId ? "Revise the page." : "Record a sighting."}
        </h1>

        <form onSubmit={submit} className="mt-14 space-y-14" data-testid="new-post-form">
          {/* Photo */}
          <section>
            <h2 className="mb-5 flex items-baseline gap-4 font-serif text-2xl font-semibold">
              <span className="text-primary/40">01</span> The photograph
            </h2>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="photo-file-input" />
            {imageUrl ? (
              <div className="relative border border-border" data-testid="photo-preview">
                <img src={imgSrc(imageUrl)} alt="Upload preview" className="max-h-96 w-full object-cover" />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="photo-change-button"
                  className="absolute bottom-4 right-4 bg-background/90 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] backdrop-blur-md transition-colors hover:bg-background"
                >
                  Change photo
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                data-testid="photo-upload-button"
                className="flex h-56 w-full flex-col items-center justify-center gap-3 border border-dashed border-border bg-card text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
              >
                <ImagePlus className="h-8 w-8" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">{uploading ? "Uploading…" : "Choose a photo"}</span>
              </button>
            )}
            <div className="mt-4">
              <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">or paste an image link</label>
              <input
                value={imageUrl}
                onChange={(e) => { setImageUrl(e.target.value); setPhotoFile(null); setAiSuggestion(null); }}
                data-testid="photo-url-input"
                placeholder="https://…"
                className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
              />
            </div>
            {imageUrl && (
              <div className="mt-4" data-testid="identify-section">
                <button
                  type="button"
                  onClick={identifySpecies}
                  disabled={identifying}
                  data-testid="identify-button"
                  className="flex items-center gap-2 border border-foreground px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-foreground hover:text-background disabled:opacity-60"
                >
                  <Sparkles className={`h-4 w-4 ${identifying ? "animate-pulse" : ""}`} />
                  {identifying
                    ? aiProgress != null
                      ? `Downloading browser AI… ${aiProgress}%`
                      : "Studying the photo…"
                    : "Identify species with AI"}
                </button>
                <p className="mt-2 text-xs text-muted-foreground" data-testid="identify-hint">
                  Runs on-device in Chrome 148+ or Edge — free, private, no credits used
                </p>
                {cloudOffer && (
                  <button
                    type="button"
                    onClick={identifyWithCloud}
                    disabled={identifying}
                    data-testid="identify-cloud-button"
                    className="mt-3 flex items-center gap-2 border border-primary px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-primary transition-colors duration-300 hover:bg-primary hover:text-primary-foreground disabled:opacity-60"
                  >
                    <Sparkles className="h-4 w-4" />
                    Use cloud AI instead (shared credits)
                  </button>
                )}
                {aiSuggestion && (
                  <p className="mt-3 border-l-2 border-primary pl-4 text-sm text-muted-foreground" data-testid="ai-suggestion">
                    <span className="font-bold text-foreground">{aiSuggestion.species}</span>
                    {aiSuggestion.confidence ? ` · ${aiSuggestion.confidence} confidence` : ""}
                    {aiSuggestion.note ? ` — ${aiSuggestion.note}` : ""}
                  </p>
                )}
              </div>
            )}

            <div className="mt-8">
              <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                More photos of this animal (optional)
              </label>
              <div className="flex flex-wrap gap-3" data-testid="extra-photos-list">
                {extraPhotos.map((p, i) => (
                  <div key={i} className="relative h-20 w-20 border border-border" data-testid={`extra-photo-${i}`}>
                    <img src={imgSrc(p)} alt={`Additional photo ${i + 1}`} className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setExtraPhotos((prev) => prev.filter((_, idx) => idx !== i))}
                      data-testid={`extra-photo-remove-${i}`}
                      className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-xs text-destructive-foreground"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <label
                  data-testid="add-extra-photos-button"
                  className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 border border-dashed border-border text-muted-foreground transition-colors duration-300 hover:border-primary hover:text-primary"
                >
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => { handleExtraFiles(e.target.files); e.target.value = ""; }}
                    data-testid="extra-photos-input"
                  />
                  <Plus className="h-5 w-5" />
                  <span className="text-[9px] font-bold uppercase tracking-widest">{uploading ? "…" : "Add"}</span>
                </label>
              </div>
              <div className="mt-3 flex gap-2">
                <input
                  value={extraUrl}
                  onChange={(e) => setExtraUrl(e.target.value)}
                  data-testid="extra-photo-url-input"
                  placeholder="or paste another image link…"
                  className="flex-1 border border-border bg-card px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary"
                />
                <button
                  type="button"
                  onClick={addExtraPhotoUrl}
                  data-testid="extra-photo-url-add"
                  className="border border-foreground px-4 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-foreground hover:text-background"
                >
                  Add
                </button>
              </div>
            </div>
          </section>

          {/* Who */}
          <section>
            <h2 className="mb-5 flex items-baseline gap-4 font-serif text-2xl font-semibold">
              <span className="text-primary/40">02</span> Who did you meet?
            </h2>
            {editId && user?.role === "admin" && (
              <div className="mb-5 border border-primary/40 bg-primary/5 p-4" data-testid="author-admin-block">
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-primary">
                  Entry author (admin only)
                </label>
                <select
                  value={authorId}
                  onChange={(e) => setAuthorId(e.target.value)}
                  data-testid="author-select"
                  className="w-full border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                >
                  {authorId && !usersList.some((u) => u.user_id === authorId) && (
                    <option value={authorId}>Current author (not a member account)</option>
                  )}
                  {usersList.map((u) => (
                    <option key={u.user_id} value={u.user_id}>
                      {u.name} — {u.email}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-muted-foreground">Reassigns this entry's byline to the selected member.</p>
              </div>
            )}
            <div className="grid gap-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Entry title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="title-input"
                  required
                  placeholder="The Early Riser of the Quad"
                  className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Species</label>
                <input
                  value={species}
                  onChange={(e) => setSpecies(e.target.value)}
                  data-testid="species-input"
                  placeholder="American Robin"
                  className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                />
              </div>
              <div>
                <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="category-select"
                  className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* Where */}
          <section>
            <h2 className="mb-5 flex items-baseline gap-4 font-serif text-2xl font-semibold">
              <span className="text-primary/40">03</span> Where on campus?
            </h2>
            <div className="mb-5">
              <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Place name</label>
              <input
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                data-testid="location-name-input"
                placeholder="Oak trees by the library"
                className="w-full border border-border bg-card px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
              />
            </div>
            <p className="mb-3 text-xs text-muted-foreground">Tap the map to drop a pin on the exact spot.</p>
            <LocationPicker position={position} onPick={setPosition} />
          </section>

          {/* Writings */}
          <section>
            <h2 className="mb-5 flex items-baseline gap-4 font-serif text-2xl font-semibold">
              <span className="text-primary/40">04</span> The writing
            </h2>
            <div className="space-y-8">
              {chapters.map((ch, i) => (
                <div key={i} className="relative border border-border bg-card p-6" data-testid={`chapter-editor-${i}`}>
                  {chapters.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setChapters((prev) => prev.filter((_, idx) => idx !== i))}
                      data-testid={`chapter-remove-${i}`}
                      className="absolute right-4 top-4 text-muted-foreground transition-colors hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  <label className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    Chapter {String(i + 1).padStart(2, "0")} heading
                  </label>
                  <input
                    value={ch.heading}
                    onChange={(e) => setChapter(i, "heading", e.target.value)}
                    data-testid={`chapter-heading-input-${i}`}
                    placeholder="The First Song"
                    className="mb-4 w-full border border-border bg-background px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                  />
                  <textarea
                    value={ch.body}
                    onChange={(e) => setChapter(i, "body", e.target.value)}
                    data-testid={`chapter-body-input-${i}`}
                    rows={5}
                    placeholder="Write what you saw, heard, or wondered about…"
                    className="w-full resize-none border border-border bg-background p-4 text-sm leading-relaxed outline-none transition-colors focus:border-primary"
                  />
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setChapters((prev) => [...prev, { heading: "", body: "" }])}
              data-testid="add-chapter-button"
              className="mt-5 flex items-center gap-2 border border-foreground px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-foreground hover:text-background"
            >
              <Plus className="h-4 w-4" /> Add another chapter
            </button>
          </section>

          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-entry-button"
            className="w-full bg-primary px-6 py-5 text-xs font-bold uppercase tracking-[0.3em] text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
          >
            {submitting ? "Binding the page…" : editId ? "Save changes" : "Publish to the journal"}
          </button>
        </form>
      </motion.main>
    </div>
  );
}
